/**
 * What the server said it actually served, per column.
 *
 * The `columns` metadata frame carries one extra string field — `source`, one of
 * `stored` | `client` | `compute` — and it is an **acknowledgement**, not a
 * description. `qf-us` is the only party that knows whether a computed column
 * was forwarded anywhere, so it says so; a column the client asked for that does
 * not come back acknowledged has no source at all.
 *
 * **"No source" is deliberately not a fourth value.** Absence is the signal, and
 * that is what keeps *nobody is computing this* distinguishable from *not
 * computed yet*. Before two-wave delivery an empty column unambiguously meant no
 * source; once computed columns arrive late it means either, and the same pixels
 * would spin forever over a column that is never coming. See
 * `docs/screener-compute-design.html` §6.
 *
 * Read here rather than in `@qufolio/transport`'s `decodeColumnMeta` because the
 * generic frame decode already carries every field of every frame through as
 * plain row objects — an additive wire field is reachable with no decoder
 * change. A server that predates the field yields an empty map, and every
 * history-needing computed column is then correctly *unacknowledged*: which is
 * exactly phase 1, where no compute service exists and the honest answer is that
 * nothing is going to send those values.
 */

import { COLUMNS_FRAME, type ScreenerFrame } from "@qufolio/transport";

import type { ColumnOrigin } from "./column-universe.js";

/**
 * The vocabulary the wire and `ColumnOrigin` share.
 *
 * One set, so a value the server invents cannot be read as an acknowledgement:
 * a source this client has never heard of cannot be reasoned about, and
 * treating it as one would render "waiting" for a service nobody here knows.
 */
const KNOWN_SOURCES = new Set<string>(["stored", "client", "compute"]);

/**
 * The `source` each column came back marked with. Fields the server said
 * nothing about are simply absent — the caller must read absence as "no
 * source", never as a default.
 */
export function readColumnSources(
  frames: readonly ScreenerFrame[] | undefined,
): Map<string, ColumnOrigin> {
  const out = new Map<string, ColumnOrigin>();
  const frame = frames?.find((f) => f.name === COLUMNS_FRAME);
  if (!frame) return out;

  for (const row of frame.rows) {
    const field = typeof row["field"] === "string" ? row["field"] : "";
    const source = typeof row["source"] === "string" ? row["source"] : "";
    if (!field || !KNOWN_SOURCES.has(source)) continue;
    out.set(field, source as ColumnOrigin);
  }
  return out;
}
